import { ReactFlowProvider } from "@xyflow/react";
import { ConfigProvider, Spin } from "antd";
import { useEffect, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useTranslation } from "react-i18next";
import { useLocation, useRoutes } from "react-router-dom";

import { LanguageLoadingContainer } from "./App.styles";
import { CacheBusterWrapper } from "./components/CacheBusterWrapper";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Modal } from "./components/Modal";
import { getAntdLocale } from "./config/antdLocale";
import { ToastProvider } from "./contexts/ToastContext";
import { usePortalTranslations } from "./hooks/queries/translations";
import { useAuth } from "./hooks/useAuth";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import { useModal } from "./hooks/useModal";
import { globalStyles } from "./layout/styles/global";
import { NotFound } from "./pages/NotFound";
import { routes } from "./routes/routes";
import "./config/i18n";
import { css, rehydrateColorsPatchPortal } from "./styles";
import {
  isI18nSourceDebugEnabled,
  startI18nSourceHighlight,
} from "./utils/i18nSourceDebug";

const containerStyles = css({
  width: "100%",
  height: "100vh",
  fontFamily: "$openSans",
});

// TODO: try to remove all the any values.
// TODO: Improve all the types from the app
// TODO: Add history and redirects

function App() {
  const routeElements = useRoutes(routes);
  const location = useLocation();
  const {
    isModalOpen,
    modalContent,
    closeModal,
    optionsModal,
    sidePanelContent,
    sidePanelOptions,
  } = useModal();

  const { isAuthenticated, signOut } = useAuth();

  // EN-5191: pulls the portal's published UI strings over the built-in en-GB baseline.
  const { isPortalTranslationsReady } = usePortalTranslations();

  // EN-5191: `?i18nDebug=1` marks and reddens the strings that came from the API, so the
  // wiring can be checked without editing rows one at a time. No-op unless asked for.
  useEffect(() => {
    if (!isI18nSourceDebugEnabled()) return undefined;
    return startI18nSourceHighlight();
  }, []);
  const idleTime: number =
    Number(String(process.env.REACT_APP_IDLE_TIMEOUT_IN_MINUTES)) || 15;

  const { i18n } = useTranslation();

  const componentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isModalOpen && componentRef.current) {
      componentRef.current.blur();
    }
  }, [isModalOpen]);

  useIdleTimeout({
    onIdle: () => {
      if (isAuthenticated) {
        signOut();
      }
    },
    idleTime,
  });

  globalStyles();

  rehydrateColorsPatchPortal();

  return (
    <CacheBusterWrapper isEnabled isVerboseMode={false} metaFileDirectory=".">
      {/* antd renders its own strings (date-picker months, pagination, empty states) from its
          locale bundle rather than through i18next, so it needs telling separately. */}
      <ConfigProvider locale={getAntdLocale(i18n.language)}>
        <ToastProvider>
          <div className={containerStyles()} ref={componentRef}>
            <Modal
              open={isModalOpen}
              optionsModal={optionsModal}
              sidePanel={sidePanelContent}
              sidePanelOptions={sidePanelOptions}
              onOpenChange={() => {
                if (isModalOpen) {
                  closeModal();
                }
              }}
            >
              {modalContent}
            </Modal>
            <ReactFlowProvider>
              <DndProvider backend={HTML5Backend}>
                {/* Outermost net: catches crashes in DashboardLayout itself
                    and in the full-screen routes that have no layout. Page-level
                    crashes are caught closer to the page, inside
                    DashboardLayout, so they keep the portal chrome.

                    `resetKey`, not `key`: a changed key would remount the whole
                    route tree on every navigation. */}
                <ErrorBoundary
                  resetKey={location.pathname}
                  fallback={<NotFound reason="error" />}
                >
                  {/* EN-5191: a non-English session has no bundle until the API answers, so
                      rendering here would paint English and then repaint translated — the
                      signed-in user would watch the portal change language under them. Holding
                      the first paint costs a spinner once per tab (the query is cached for the
                      tab's lifetime). The gate opens on error, so a failed request degrades to
                      English rather than to a portal that never renders.

                      Signed-in only, and that is load-bearing: /surveys/answer/:userSurveyId is
                      the mobile webview, deliberately outside the auth guard, and it renders
                      from the survey's own fixed translations rather than this bundle. Gating it
                      would make a patient on a mobile connection wait on a request their screen
                      never reads. */}
                  {!isAuthenticated || isPortalTranslationsReady ? (
                    routeElements
                  ) : (
                    <LanguageLoadingContainer>
                      <Spin size="large" />
                    </LanguageLoadingContainer>
                  )}
                </ErrorBoundary>
              </DndProvider>
            </ReactFlowProvider>
          </div>
        </ToastProvider>
      </ConfigProvider>
    </CacheBusterWrapper>
  );
}
export default App;
